import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Mail, MessagesSquare, Landmark, Home, ClipboardList, Clapperboard } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Editable } from "@/components/builder/Editable";
import { useContent } from "@/context/ContentContext";
import { trackEvent } from "@/lib/analytics";

export const VIDEO_ITEMS = [
  { key: "posta", icon: Mail, title: "Posta intelligente", desc: "L'AI legge email e PEC, le riassume e le classifica automaticamente.", defaultUrl: "/videos/posta-intelligente.mp4" },
  { key: "chat", icon: MessagesSquare, title: "Chat AI", desc: "Chiedi in linguaggio naturale: l'agente cerca ed esegue le azioni per te.", defaultUrl: "/videos/chat-ai.mp4" },
  { key: "catasto", icon: Landmark, title: "Aggiornamento catastale", desc: "Recupero e aggiornamento automatico dei dati catastali di ogni immobile.", defaultUrl: "/videos/aggiornamento-catastale.mp4" },
  { key: "stime", icon: Home, title: "Stime immobili", desc: "Stima del valore di mercato di ogni singola unità del condominio.", defaultUrl: "/videos/stime-immobili.mp4" },
  { key: "pratiche", icon: ClipboardList, title: "Pratiche AI", desc: "Apertura automatica delle pratiche e proposta dei fornitori giusti.", defaultUrl: "/videos/pratiche-ai.mp4" },
];

// Converte un URL (YouTube, Vimeo, Loom, mp4...) nella sorgente adatta all'embed.
function resolveVideo(url) {
  const u = String(url || "").trim();
  if (!u) return null;
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return { type: "iframe", src: `https://player.vimeo.com/video/${vimeo[1]}` };
  const loom = u.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return { type: "iframe", src: `https://www.loom.com/embed/${loom[1]}` };
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(u)) return { type: "video", src: u };
  return { type: "iframe", src: u };
}

export const VideoShowcase = () => {
  const { getText } = useContent();
  const [active, setActive] = useState(null); // { key, title, media }

  const openVideo = (v, media) => {
    if (!media) return;
    const title = getText(`video.${v.key}.title`, v.title);
    trackEvent("video_open", {
      video_key: v.key,
      video_title: title,
      video_url: media.src,
    });
    setActive({ key: v.key, title, media });
  };

  return (
    <section id="video" data-testid="video-showcase" className="section-lavender">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <p className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">
            <Clapperboard className="h-4 w-4" /> <Editable path="video.eyebrow">Sedio in azione</Editable>
          </p>
          <Editable as="h2" path="video.title" className="block font-display text-3xl font-extrabold text-foreground sm:text-4xl">
            Guarda l'intelligenza artificiale di Sedio al lavoro.
          </Editable>
          <Editable as="p" path="video.subtitle" className="mx-auto mt-4 block max-w-xl text-base leading-7 text-muted-foreground">
            Brevi video reali delle funzioni AI di app.sedio.it: dalla posta intelligente alle pratiche, fino a catasto e stime.
          </Editable>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {VIDEO_ITEMS.map((v, i) => {
            const media = resolveVideo(getText(`video.${v.key}.url`, v.defaultUrl || ""));
            return (
              <motion.div
                key={v.key}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`video-card-${v.key}`}
                className="group overflow-hidden rounded-2xl border border-border bg-white shadow-sm"
              >
                <div className="relative aspect-video overflow-hidden bg-[hsl(226_47%_10%)]">
                  {media ? (
                    <button
                      type="button"
                      onClick={() => openVideo(v, media)}
                      aria-label={`Guarda il video: ${v.title}`}
                      data-testid={`video-open-${v.key}`}
                      className="absolute inset-0 block h-full w-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
                    >
                      {media.type === "video" ? (
                        <video src={`${media.src}#t=0.5`} muted playsInline preload="metadata" className="pointer-events-none h-full w-full object-cover" data-testid={`video-preview-${v.key}`} />
                      ) : (
                        <div className="pointer-events-none absolute inset-0 bg-grid-faint opacity-[0.12]" aria-hidden="true" />
                      )}
                      <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors duration-200 group-hover:bg-black/10">
                        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-cyan-400/90 text-slate-900 shadow-lg ring-4 ring-white/20 transition-transform duration-200 group-hover:scale-110">
                          <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" strokeWidth={0} />
                        </span>
                      </span>
                    </button>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center" data-testid={`video-placeholder-${v.key}`}>
                      <div className="absolute inset-0 bg-grid-faint opacity-[0.12]" aria-hidden="true" />
                      <span className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-cyan-400/20 text-cyan-200 ring-1 ring-white/15 transition-transform duration-200 group-hover:scale-110">
                        <Play className="h-6 w-6 translate-x-0.5" fill="currentColor" strokeWidth={0} />
                      </span>
                      <span className="relative text-xs font-medium uppercase tracking-[0.14em] text-slate-300">Video in arrivo</span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary"><v.icon className="h-4 w-4" strokeWidth={2} /></span>
                    <h3 className="font-display text-lg font-bold text-foreground"><Editable path={`video.${v.key}.title`}>{v.title}</Editable></h3>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground"><Editable path={`video.${v.key}.desc`}>{v.desc}</Editable></p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Lightbox video */}
      <Dialog open={!!active} onOpenChange={(open) => !open && setActive(null)}>
        <DialogContent
          data-testid="video-lightbox"
          className="max-w-5xl overflow-hidden border-white/10 bg-[hsl(226_47%_8%)] p-0 shadow-2xl sm:rounded-2xl"
        >
          <DialogTitle className="sr-only">{active?.title || "Video"}</DialogTitle>
          {active && (
            <div className="flex flex-col">
              <div className="relative aspect-video w-full bg-black">
                {active.media.type === "video" ? (
                  <video
                    src={active.media.src}
                    controls
                    autoPlay
                    playsInline
                    className="h-full w-full object-contain"
                    data-testid="video-lightbox-player"
                  />
                ) : (
                  <iframe
                    src={active.media.src}
                    title={active.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full"
                    data-testid="video-lightbox-player"
                  />
                )}
              </div>
              <div className="flex items-center gap-2 px-5 py-3.5">
                <Clapperboard className="h-4 w-4 text-cyan-300" />
                <p className="text-sm font-semibold text-white" data-testid="video-lightbox-title">{active.title}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
